import { useState } from "react";
import { db } from "./firebase";
import { doc, setDoc } from "firebase/firestore";

const METRICS = [
  { key: "weight", label: "Weight", emoji: "⚖️" },
  { key: "measurements", label: "Measurements", emoji: "📏" },
  { key: "photos", label: "Progress Photos", emoji: "📸" },
];

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DEFAULT_CONFIG = {
  weight:       { xp: 10,  enabled: false, scheduleDays: [], intervalWeeks: 1 },
  measurements: { xp: 25,  enabled: false, scheduleDays: [], intervalWeeks: 2 },
  photos:       { xp: 50,  enabled: false, scheduleDays: [], intervalWeeks: 4 },
};

function AdminBodyMetricsConfig({ selectedAdminUser, onSaved }) {
  const existing = selectedAdminUser.bodyMetricsConfig || {};
  const initial = {
    weight:       { ...DEFAULT_CONFIG.weight,       ...(existing.weight       || {}) },
    measurements: { ...DEFAULT_CONFIG.measurements, ...(existing.measurements || {}) },
    photos:       { ...DEFAULT_CONFIG.photos,       ...(existing.photos       || {}) },
  };

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!draft) {
    return (
      <button
        type="button"
        onClick={() => setDraft(initial)}
        style={{
          padding: "10px 16px",
          borderRadius: "12px",
          border: "2px solid #bfdbfe",
          background: "#f5faff",
          color: "#1e3a5f",
          fontWeight: "bold",
          fontSize: "13px",
          cursor: "pointer",
        }}
      >
        ✏️ Edit Body Metrics Config
      </button>
    );
  }

  function updateMetric(key, field, value) {
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  }

  function toggleDay(key, day) {
    const days = draft[key].scheduleDays || [];
    const updated = days.includes(day)
      ? days.filter((d) => d !== day)
      : [...days, day].sort((a, b) => a - b);
    updateMetric(key, "scheduleDays", updated);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await setDoc(
        doc(db, "users", selectedAdminUser.id),
        { bodyMetricsConfig: draft },
        { merge: true }
      );
      setDraft(null);
      if (onSaved) onSaved(draft);
    } catch (err) {
      console.error("Error saving body metrics config:", err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: "12px" }}>
      {METRICS.map(({ key, label, emoji }) => {
        const cfg = draft[key];
        return (
          <div
            key={key}
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "14px",
              border: "1px solid #bfdbfe",
            }}
          >
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: cfg.enabled ? "12px" : 0 }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>
                {emoji} {label}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#4b6b8a", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={cfg.enabled}
                  onChange={(e) => updateMetric(key, "enabled", e.target.checked)}
                />
                XP enabled
              </label>
            </div>

            {cfg.enabled && (
              <div style={{ display: "grid", gap: "10px" }}>
                {/* XP Amount */}
                <div>
                  <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                    XP per log
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={cfg.xp}
                    onChange={(e) => updateMetric(key, "xp", Number(e.target.value))}
                    style={{
                      width: "90px",
                      padding: "6px 10px",
                      borderRadius: "8px",
                      border: "1px solid #bfdbfe",
                      fontSize: "13px",
                      color: "#1e3a5f",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* Schedule days */}
                <div>
                  <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "6px" }}>
                    Reminder days (optional)
                  </div>
                  <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                    {DAY_LABELS.map((day, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => toggleDay(key, i)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "8px",
                          border: cfg.scheduleDays.includes(i) ? "2px solid #38bdf8" : "1px solid #bfdbfe",
                          background: cfg.scheduleDays.includes(i) ? "#e0f2fe" : "#f8fbff",
                          color: cfg.scheduleDays.includes(i) ? "#0369a1" : "#4b6b8a",
                          fontSize: "12px",
                          fontWeight: cfg.scheduleDays.includes(i) ? "bold" : "normal",
                          cursor: "pointer",
                        }}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: "11px", marginTop: "4px" }}>
                    Select days to show a reminder on their dashboard. XP is earned any time they log.
                  </div>
                </div>

                {/* Interval — only shown when days are set */}
                {cfg.scheduleDays.length > 0 && (
                  <div>
                    <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                      Frequency
                    </div>
                    <select
                      value={cfg.intervalWeeks}
                      onChange={(e) => updateMetric(key, "intervalWeeks", Number(e.target.value))}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "8px",
                        border: "1px solid #bfdbfe",
                        fontSize: "13px",
                        color: "#1e3a5f",
                        background: "#fff",
                      }}
                    >
                      <option value={1}>Every week</option>
                      <option value={2}>Every 2 weeks</option>
                      <option value={4}>Every 4 weeks</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div style={{ display: "flex", gap: "8px" }}>
        <button
          type="button"
          disabled={saving}
          onClick={handleSave}
          style={{
            padding: "10px 16px",
            borderRadius: "12px",
            border: "none",
            background: "#38bdf8",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "13px",
            cursor: "pointer",
          }}
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setDraft(null)}
          style={{
            padding: "10px 16px",
            borderRadius: "12px",
            border: "2px solid #bfdbfe",
            background: "#f5faff",
            color: "#4b6b8a",
            fontWeight: "bold",
            fontSize: "13px",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default AdminBodyMetricsConfig;
